import { useTheme } from '@repo/theme';
import { EmptyTableBanner } from 'component/dashboard/empty-table-banner';
import { LoadingSpinner } from 'component/progress/loading-spinner';
import { UtilityText } from 'component/text';
import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Catcher } from 'system/catcher';
import { labelToKey, type TLabel, t } from 'util/stdtypes';

type TableGridProps<T> = {
    itemList: T[];
    itemKey: (item: T) => string;
    columns: TableColumn<T>[];
    loading?: boolean;
    emptyWidget?: React.ReactNode;
};

const defaultEmptyWidget = <EmptyTableBanner label={t('table_grid.empty', 'Nothing here yet')} />;

function totalSpan<T>(columns: TableColumn<T>[]) {
    return columns.reduce((sum, col) => sum + (col.span ?? 1), 0);
}

export function TableGrid<T>({ itemList, itemKey, columns = [], loading, emptyWidget }: TableGridProps<T>) {
    const { colors } = useTheme();
    const hasData = !loading && itemList.length > 0;
    const total = totalSpan(columns);
    const trStyle = useMemo<React.CSSProperties>(
        () => ({ borderTop: `1px solid ${colors.colorBorderPrimary}` }),
        [colors.colorBorderPrimary],
    );
    return (
        <table style={tableStyle}>
            <thead>
                <tr>
                    {columns.map((column, colIndex) => {
                        const isLast = colIndex === columns.length - 1;
                        const align = isLast || column.alignRight;
                        return (
                            <th
                                colSpan={hasData ? (column.span ?? 1) : undefined}
                                key={labelToKey(column.label)}
                                style={{
                                    ...thStyle,
                                    ...(!hasData && { width: `${((column.span ?? 1) / total) * 100}%` }),
                                    ...(isLast && { paddingRight: 0 }),
                                    ...(align && { textAlign: 'right' }),
                                }}
                            >
                                <UtilityText caps color={colors.colorTextSecondary} label={column.label} type='small' />
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {loading ? (
                    <tr>
                        <td colSpan={columns.length} style={emptyTdStyle}>
                            <View style={TableGridStyle.loadingContainer}>
                                <LoadingSpinner />
                            </View>
                        </td>
                    </tr>
                ) : itemList.length === 0 ? (
                    <tr>
                        <td colSpan={columns.length} style={emptyTdStyle}>
                            {emptyWidget ?? defaultEmptyWidget}
                        </td>
                    </tr>
                ) : (
                    itemList.map((item, index) => (
                        <tr key={itemKey(item)} style={index > 0 ? trStyle : undefined}>
                            {columns.map((column, colIndex) => {
                                const isLast = colIndex === columns.length - 1;
                                const align = isLast || column.alignRight;
                                return (
                                    <td
                                        colSpan={column.span ?? 1}
                                        key={labelToKey(column.label)}
                                        style={{
                                            ...tdStyle,
                                            ...(isLast && { paddingRight: 0 }),
                                        }}
                                    >
                                        <div style={align ? cellAlignRight : column.shrink ? cellShrinkStyle : cellStyle}>
                                            <Catcher>
                                                <column.widget item={item} />
                                            </Catcher>
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    );
}

const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
};

const thStyle: React.CSSProperties = {
    textAlign: 'left',
    fontWeight: 'normal',
    paddingBottom: 8,
    paddingRight: 16,
};

const tdStyle: React.CSSProperties = {
    padding: '16px 16px 16px 0',
    verticalAlign: 'middle',
};

const emptyTdStyle: React.CSSProperties = {
    paddingTop: 8,
};

const cellStyle: React.CSSProperties = {
    width: 'fit-content',
};

const cellShrinkStyle: React.CSSProperties = {
    overflow: 'hidden',
};

const cellAlignRight: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
};

const TableGridStyle = StyleSheet.create({
    loadingContainer: {
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
export type TableColumnWidgetProps<T> = {
    key?: string;
    item: T;
};
export type TableColumn<T> = {
    label: TLabel;
    alignRight?: boolean;
    widget: React.ComponentType<TableColumnWidgetProps<T>>;
    span?: number;
    shrink?: boolean;
};
